// AdminPanel/OrgIdFinder — the Search tab's "Org ID" lookup: paste an org /
// tenant id, jump to that customer's details dialog.
//
// Backed by ListOrganizationsV3 (GET /api/v3/organizations?search=), whose
// `search` matches `t.id = :search` EXACTLY. No dedicated endpoint: the list
// already answers this question from an indexed column, and a second endpoint
// would be a second way to ask it. Contrast ApiKeyFinder, which genuinely
// needs its own — it matches a SHA256 hash no list column carries.
import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {Search} from '@mui/icons-material';
import {
  Alert, Box, Button, CircularProgress, TextField, Typography
} from '@mui/material';

import {adminLookupOrgs} from '../../actions/adminPanel';
import {validateOrgId} from './orgSearch';
import OrgResultCard from './OrgResultCard';

class OrgIdFinder extends Component {
  state = {query: '', searching: false, results: null, error: null};

  // Guards against a stale response landing after a newer search (or
  // unmount) — only the latest request may write state.
  searchSeq = 0;

  componentWillUnmount() {
    this.searchSeq++;
  }

  search = async () => {
    const check = validateOrgId(this.state.query);
    if (!check.ok) {
      this.setState({error: check.error, results: null});
      return;
    }

    const seq = ++this.searchSeq;
    this.setState({searching: true, results: null, error: null});
    try {
      const orgs = await adminLookupOrgs(check.value);
      if (this.searchSeq !== seq) return;
      this.setState({searching: false, results: orgs});
    } catch (err) {
      if (this.searchSeq !== seq) return;
      const status = err?.response?.status;
      const msg = status === 403
        ? 'You don\'t have permission to use admin search.'
        : err?.response?.data?.message || err?.message || 'Search failed — please try again.';
      this.setState({searching: false, error: msg});
    }
  };

  render() {
    const {query, searching, error, results} = this.state;
    return (
      <Box>
        <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
          Paste an org / tenant id to jump straight to that customer. The id is
          matched exactly — a partial id will not match.
        </Typography>
        <Box sx={{display: 'flex', gap: 1, mb: 2}}>
          <TextField
            fullWidth
            size="small"
            label="Org ID"
            placeholder="378884552388509999"
            value={query}
            onChange={(e) => this.setState({query: e.target.value, error: null})}
            onKeyDown={(e) => { if (e.key === 'Enter') this.search(); }}
            disabled={searching}
          />
          <Button
            variant="contained"
            onClick={this.search}
            disabled={searching}
            startIcon={searching ? <CircularProgress size={16} color="inherit" /> : <Search />}
            sx={{flexShrink: 0}}
          >
            Search
          </Button>
        </Box>
        {error && <Alert severity="error" sx={{mb: 2}}>{error}</Alert>}
        {results && results.length === 0 && (
          <Alert severity="info">
            No org with that id. Ids are matched exactly — check the complete value was pasted.
          </Alert>
        )}
        {results && results.map((o) => (
          <OrgResultCard key={o.id} org={o} orgs={this.props.orgs} onOpenOrg={this.props.onOpenOrg} />
        ))}
      </Box>
    );
  }
}

OrgIdFinder.propTypes = {
  orgs: PropTypes.array,
  onOpenOrg: PropTypes.func.isRequired
};

export default OrgIdFinder;
